/**
 * CRA-Check — one traffic light per ausgelieferte Produktinstanz.
 *
 * Two obligations have to hold at the same time, and the page keeps them apart
 * because they fail for different reasons and are fixed in different places:
 *
 *   Produktreife  — is the machinery even able to see vulnerabilities?
 *                   (Konfiguration, CPE-Identität, SBOM, Scan-Alter, Triage)
 *   Meldefristen  — for everything BETROFFEN: are the 24 h / 72 h / 14 d clocks
 *                   intact and has the customer been told?
 *
 * `unbekannt` is NOT green. A check that could not run (Dependency-Track down)
 * has not passed, so it is reported as its own state and counted separately — a
 * DT outage must never read as "alles konform".
 *
 * Everything shown here is derived server-side
 * (app/services/cra_check_service.py); this page adds no rules of its own, so the
 * verdict cannot drift between API consumers.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import CveLink from '../components/CveLink';
import Banner from '../components/Banner';
import Chip, { ChipList } from '../components/Chip';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import { Icon } from '../components/Icon';
import KpiDistribution, { type DistributionItem } from '../components/KpiDistribution';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import { errorMessage, fetchCraCheckOverview } from '../lib/api';
import { DASH, countLabel, fmtDate } from '../lib/format';
import type { ChipTone } from '../components/Chip';
import type {
  CraCheck,
  CraCheckOverviewResponse,
  CraCheckRow,
  CraDeadline,
  CraState,
} from '../lib/types';
import { CRA_STATE_LABELS } from '../lib/types';

import './cracheck/cracheck.css';

const STATE_CHIP: Readonly<Record<CraState, ChipTone>> = {
  ok: 'good',
  warnung: 'warn',
  fehler: 'alert',
  unbekannt: 'idle',
};

/** Sort order for the state filter buttons and the table. */
const STATE_RANK: Readonly<Record<CraState, number>> = {
  fehler: 0,
  unbekannt: 1,
  warnung: 2,
  ok: 3,
};

/** Tooltip on every „Unbekannt“ chip: a check that did not run has not passed. */
const UNKNOWN_TITLE = 'Prüfung konnte nicht ausgeführt werden. Zählt nicht als konform.';

/** CRA state chip; „Unbekannt“ carries the reason as its tooltip. `plain` is
 *  the text variant for the secondary Reife/Meldefristen columns. */
function StateChip({ state, plain = false }: { state: CraState; plain?: boolean }): ReactNode {
  return (
    <Chip tone={STATE_CHIP[state]} plain={plain} title={state === 'unbekannt' ? UNKNOWN_TITLE : undefined}>
      {CRA_STATE_LABELS[state]}
    </Chip>
  );
}

/**
 * The version part of a ProduktVersion label. CMDB labels a version as
 * "<Produkt> v<Nr>", so next to the product name only "v<Nr>" is new.
 */
function versionSuffix(produkt: string, produktversion: string): string {
  const version = produktversion.trim();
  const name = produkt.trim();
  if (name && version.toLowerCase().startsWith(name.toLowerCase())) {
    return version.slice(name.length).trim();
  }
  return version;
}

/** '4,5 h', '2 Tage', or 'seit 6,0 h überfällig'. */
function fmtRemaining(deadline: CraDeadline): string {
  const hours = Math.abs(deadline.remaining_hours);
  const text =
    hours >= 48
      ? `${Math.round(hours / 24)} Tage`
      : `${hours.toFixed(1).replace('.', ',')} h`;
  return deadline.overdue ? `seit ${text} überfällig` : `in ${text}`;
}

function CheckList({ checks }: { checks: readonly CraCheck[] }): ReactNode {
  return (
    <ul className="cra-checks">
      {checks.map((check) => (
        <li key={check.key}>
          <StateChip state={check.state} />
          <div>
            <div className="cra-check-label">{check.label}</div>
            <div className="cra-check-detail">{check.detail}</div>
          </div>
          {check.hint && check.state !== 'ok' ? (
            <div className="cra-check-hint">
              <Icon name="arrow-right" size={12} /> {check.hint}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** Status filter of the table: all, „Mit Befund“, one CRA state, or an
 *  overdue reporting deadline. The legend entries set the same filter. */
type StatusFilter = 'alle' | 'befund' | CraState | 'frist';

const STATUS_OPTIONS: readonly { value: StatusFilter; label: string }[] = [
  { value: 'alle', label: 'Alle Instanzen' },
  { value: 'befund', label: 'Mit Befund' },
  { value: 'ok', label: 'Konform' },
  { value: 'fehler', label: 'Fehler' },
  { value: 'warnung', label: 'Warnung' },
  { value: 'frist', label: 'Frist überfällig' },
  { value: 'unbekannt', label: 'Nicht prüfbar' },
];

function hasOverdueDeadline(row: CraCheckRow): boolean {
  return row.deadlines.some((deadline) => deadline.overdue);
}

function matchesStatus(row: CraCheckRow, status: StatusFilter): boolean {
  if (status === 'alle') return true;
  if (status === 'befund') return row.state !== 'ok';
  if (status === 'frist') return hasOverdueDeadline(row);
  return row.state === status;
}

export function CraCheckPage(): ReactNode {
  const [data, setData] = useState<CraCheckOverviewResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState<StatusFilter>('alle');
  const [search, setSearch] = useState('');

  const load = useCallback(async (mode: 'initial' | 'refresh'): Promise<void> => {
    if (mode === 'initial') setLoading(true);
    else setRefreshing(true);
    try {
      const response = await fetchCraCheckOverview();
      setData(response);
      setLoadError(null);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load('initial');
  }, [load]);

  const rows = useMemo((): CraCheckRow[] => {
    const all = data?.rows ?? [];
    const term = search.trim().toLowerCase();
    return all.filter((row) => {
      if (!matchesStatus(row, status)) return false;
      if (term === '') return true;
      return (
        row.kunde.toLowerCase().includes(term) ||
        row.produkt.toLowerCase().includes(term) ||
        row.seriennummer.toLowerCase().includes(term) ||
        row.kev_cves.some((cve) => cve.toLowerCase().includes(term))
      );
    });
  }, [data, status, search]);

  const distribution = useMemo((): DistributionItem[] => {
    const summary = data?.summary;
    if (!summary) return [];
    return [
      { key: 'ok', label: 'Konform', value: summary.by_state.ok ?? summary.compliant, tone: 'good', inBar: true },
      { key: 'fehler', label: 'Fehler', value: summary.by_state.fehler ?? 0, tone: 'alert', inBar: true },
      { key: 'warnung', label: 'Warnungen', value: summary.by_state.warnung ?? 0, tone: 'warn', inBar: true },
      {
        // Counted per instance (like every other entry), so the filtered list
        // shows exactly this many rows; the clocks behind it go in the tooltip.
        key: 'frist',
        label: 'Frist überfällig',
        value: data.rows.filter(hasOverdueDeadline).length,
        tone: 'critical',
        title: `${summary.fristen_ueberfaellig} ${summary.fristen_ueberfaellig === 1 ? 'Meldefrist' : 'Meldefristen'} offener Benachrichtigungen verstrichen. Fristwarnung ${data.thresholds.deadline_warn_hours} h vorher.`,
      },
      {
        key: 'unbekannt',
        label: 'Nicht prüfbar',
        value: summary.by_state.unbekannt ?? 0,
        tone: 'idle',
        inBar: true,
        title:
          'Mindestens eine Prüfung konnte nicht ausgeführt werden, z. B. weil Dependency-Track nicht erreichbar ist. Zählt nicht als konform.',
      },
    ];
  }, [data]);

  // A legend entry is marked while exactly its status filter is active (search
  // empty); clicking it opens that whole group, a second click clears it.
  const legendSelected = search.trim() === '' && status !== 'alle' && status !== 'befund' ? status : null;
  const onLegendSelect = (key: string): void => {
    const next = key as StatusFilter;
    setStatus(legendSelected === next ? 'alle' : next);
    setSearch('');
  };

  const columns = useMemo(
    (): readonly DataTableColumn<CraCheckRow>[] => [
      {
        key: 'state',
        header: 'CRA',
        width: '120px',
        sortValue: (row) => STATE_RANK[row.state],
        render: (row) => <StateChip state={row.state} />,
      },
      {
        key: 'kunde',
        header: 'Kunde',
        sortValue: (row) => row.kunde.toLowerCase(),
        render: (row) => <strong>{row.kunde || DASH}</strong>,
      },
      {
        key: 'produkt',
        header: 'Produkt',
        sortValue: (row) => row.produkt.toLowerCase(),
        render: (row) => {
          const version = versionSuffix(row.produkt, row.produktversion);
          if (!row.produkt) return row.produktversion || DASH;
          return (
            <>
              {row.produkt}
              {version ? <span className="muted"> {version}</span> : null}
            </>
          );
        },
      },
      {
        key: 'sn',
        header: 'Seriennr.',
        width: '170px',
        sortValue: (row) => row.seriennummer,
        render: (row) => <span className="mono">{row.seriennummer || DASH}</span>,
      },
      {
        key: 'reife',
        header: 'Reife',
        headerTitle: 'Produktreife: Konfiguration, CPE-Identität, SBOM, Scan-Alter, Triage',
        width: '100px',
        sortValue: (row) => STATE_RANK[row.state_reife],
        render: (row) => <StateChip state={row.state_reife} plain />,
      },
      {
        key: 'fristen',
        header: 'Meldefristen',
        width: '125px',
        sortValue: (row) => STATE_RANK[row.state_fristen],
        render: (row) => <StateChip state={row.state_fristen} plain />,
      },
      {
        key: 'kev',
        header: 'KEV',
        headerTitle: 'KEV-CVEs und davon als „Betroffen“ bewertete',
        width: '190px',
        sortValue: (row) => row.betroffen_cves.length * 100 + row.kev_cves.length,
        render: (row) =>
          row.kev_cves.length === 0 && row.betroffen_cves.length === 0 ? (
            <span className="muted">{DASH}</span>
          ) : (
            <ChipList className="cra-kev">
              {row.kev_cves.length > 0 ? (
                <Chip tone="alert" count title={row.kev_cves.join(', ')}>
                  {row.kev_cves.length} KEV
                </Chip>
              ) : null}
              {row.betroffen_cves.length > 0 ? (
                <Chip tone="warn" count title={row.betroffen_cves.join(', ')}>
                  {row.betroffen_cves.length} betroffen
                </Chip>
              ) : null}
            </ChipList>
          ),
      },
    ],
    [],
  );

  const renderDetail = useCallback((row: CraCheckRow): ReactNode => {
    const reife = row.checks.filter((check) => check.group === 'reife');
    const fristen = row.checks.filter((check) => check.group === 'fristen');
    return (
      <>
        <div className="cra-groups">
          <div>
            <h3>Produktreife</h3>
            <CheckList checks={reife} />
          </div>
          <div>
            <h3>Meldefristen (CRA Art. 14)</h3>
            <CheckList checks={fristen} />
            {row.deadlines.length > 0 ? (
              <ul className="cra-deadlines">
                {row.deadlines.map((deadline) => (
                  <li key={`${deadline.benachrichtigung_id}-${deadline.clock}`}>
                    <Chip tone={deadline.overdue ? 'alert' : 'warn'}>{deadline.clock_label}</Chip>
                    <span>{fmtRemaining(deadline)}</span>
                    <span className="muted">{fmtDate(deadline.due)}</span>
                    {deadline.cves.length > 0 ? (
                      <ChipList>{deadline.cves.map((cve) => <CveLink key={cve} cve={cve} />)}</ChipList>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
        <p className="cra-detail-meta">
          {countLabel(row.komponenten_count, 'Komponentenversion', 'Komponentenversionen')} in der
          aufgelösten Konfiguration
          {row.findings_total > 0
            ? ` · ${countLabel(row.findings_total, 'Befund', 'Befunde')} in Dependency-Track`
            : ''}
          {row.project_uuid ? ` · Projekt ${row.project_uuid}` : ''}
        </p>
      </>
    );
  }, []);

  if (loading) return <PageSpinner text="Prüft CRA-Konformität…" />;

  return (
    <>
      <PageHeader
        title="CRA-Check"
        sub={
          data
            ? `Geprüft am ${fmtDate(data.generated_at)} · SBOM veraltet nach ${data.thresholds.sbom_max_age_days} Tagen, Scan nach ${data.thresholds.scan_max_age_days} Tagen`
            : undefined
        }
        actions={
          <>
            {refreshing ? <Spinner label="Aktualisiert…" /> : null}
            <button type="button" onClick={() => void load('refresh')} disabled={refreshing}>
              Neu prüfen
            </button>
          </>
        }
      />

      {loadError ? (
        <Banner kind="error" sub="Prüfen Sie die Verbindung zu CMDBuild.">
          {loadError}
        </Banner>
      ) : null}

      {data ? (
        <KpiDistribution
          total={data.summary.total}
          totalLabel="Instanzen geprüft"
          items={distribution}
          label="CRA-Konformität der Instanzen"
          selectedKey={legendSelected}
          onSelect={onLegendSelect}
          controls="cra-results"
        />
      ) : null}

      <section id="cra-results" className="card">
        <div className="card-head table-head">
          <h2>Konformität je Instanz</h2>
        </div>

        <div className="table-toolbar">
          <input
            type="search"
            value={search}
            placeholder="Kunde, Produkt, Seriennummer oder CVE suchen…"
            aria-label="CRA-Check durchsuchen"
            onChange={(event) => setSearch(event.target.value)}
          />
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as StatusFilter)}
            aria-label="Nach Status filtern"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="table-toolbar-options">
            {status !== 'alle' || search.trim() !== '' ? (
              <button type="button" className="link" onClick={() => { setStatus('alle'); setSearch(''); }}>
                Filter zurücksetzen
              </button>
            ) : null}
            <span className="table-count" aria-live="polite">
              {rows.length === (data?.rows.length ?? 0)
                ? countLabel(rows.length, 'Instanz', 'Instanzen')
                : `${rows.length} von ${countLabel(data?.rows.length ?? 0, 'Instanz', 'Instanzen')}`}
            </span>
          </div>
        </div>

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.instanz_id}
          renderDetail={renderDetail}
          mobileBreakpoint={960}
          pageSize={10}
          paginationResetKey={JSON.stringify([status, search])}
          resetPageOnRowsChange={false}
          caption="CRA-Konformität je Produktinstanz"
          emptyText={
            (data?.rows.length ?? 0) === 0
              ? 'Keine Produktinstanzen vorhanden. Unter „Stammdaten“ anlegen oder per CSV importieren.'
              : 'Keine Instanz passt zu den Filtern.'
          }
        />
      </section>
    </>
  );
}

export default CraCheckPage;
